import { api } from "@/lib/api";

const CONTENT_OPS = "/content";

export interface DashboardRow {
  id: string;
  contentType: string;
  title: string;
  categoryName: string | null;
  status: string;
  createdBy: string;
  createdByRole: string;
  createdByName: string | null;
  categoryId: string | null;
  languageCodes: string[];
  languageCount: number;
  tagCount: number;
  updatedAt: string;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  version: number;
  editUrl: string;
}

export interface DashboardCounts {
  total: number;
  statusCounts: Record<string, number>;
  typeCounts: Record<string, number>;
  typeStatusCounts: Record<string, Record<string, number>>;
  authoredCounts: { submitted: number; live: number; total: number };
  owners: { id: string; name: string; role: string }[];
}

export interface DashboardPage {
  items: DashboardRow[];
  total: number;
  page: number;
  limit: number;
}

export interface DashboardItemFilters {
  status?: string;
  type?: string;
  categoryId?: string;
  ownerId?: string;
  languageCode?: string;
  search?: string;
  sortField?: string;
  sortDir?: "asc" | "desc";
  publishedWithinDays?: number;
  page?: number;
  limit?: number;
}

export const ContentOperationService = {
  async getDashboardCounts(): Promise<DashboardCounts> {
    return api.get<DashboardCounts>(`${CONTENT_OPS}/dashboard/summary`);
  },

  async getDashboardItems(
    filters: DashboardItemFilters,
  ): Promise<DashboardPage> {
    const params = new URLSearchParams();
    const set = (key: string, value?: string) => {
      if (value !== undefined && value !== "" && value !== "all") {
        params.set(key, value);
      }
    };
    set("status", filters.status);
    set("type", filters.type);
    set("categoryId", filters.categoryId);
    set("ownerId", filters.ownerId);
    set("languageCode", filters.languageCode);
    set("search", filters.search);
    set("sortField", filters.sortField);
    set("sortDir", filters.sortDir);
    set("publishedWithinDays", filters.publishedWithinDays?.toString());
    params.set("page", String(filters.page ?? 1));
    params.set("limit", String(filters.limit ?? 10));
    return api.get<DashboardPage>(
      `${CONTENT_OPS}/dashboard?${params.toString()}`,
    );
  },

  async reviewContent(
    type: string,
    id: string,
    payload: { action: string; note?: string },
  ): Promise<any> {
    return api.post<any>(`${CONTENT_OPS}/review/${type}/${id}`, payload);
  },

  // limit=0 returns counts only (no issue rows).
  async getHealthIssues(
    params: { page?: number; limit?: number } = {},
  ): Promise<any> {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set("page", String(params.page));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return api.get<any>(
      `${CONTENT_OPS}/health-issues${query ? `?${query}` : ""}`,
    );
  },
};
